import { useState, useEffect } from 'react'
import styled, { css } from 'styled-components'
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { supabase } from '../../supabase'
import {
  t, fadeIn, scaleIn, AdminUIProvider, AdminGlobal, useUI,
  Btn, Input, Label, Field, CountBadge, Spinner,
} from './ui'

import ClientsAdmin from './Clients'
import CarnetAdmin from './Carnet'
import CarnetDetail from './CarnetDetail'
import CarnetLivraison from './CarnetLivraison'
import DevisAdmin from './Devis'
import DevisDetail from './DevisDetail'
import StatistiquesAdmin from './Stats'
import { alertesActives, basculerAlertes, jouerCarillon, notifier } from './alertes'
import ListeAttenteAdmin from './ListeAttente'
import PlanningAdmin from './Planning'
import TicketsAdmin from './Tickets'
import PrevisionnelAdmin from './Previsionnel'
import ProduitsAdmin from './Produits'
import FournisseursAdmin from './Fournisseurs'
import ModelesMailAdmin from './ModelesMail'
import AvisAdmin from './Avis'
import FeedbackAdmin from './Feedback'

const ease = 'cubic-bezier(.22,.9,.3,1)'

const NAV = [
  {
    group: 'Suivi client',
    items: [
      {
        to: 'clients', label: 'Clients', icon: '👥',
        alert: 'rappel', alertIcon: '🔔', alertTone: 'amber',
        alertTitle: (n) => `${n} rappel${n > 1 ? 's' : ''} client à faire`,
      },
      {
        to: 'carnet', label: 'Carnet de commandes', icon: '🚛',
        alert: 'validation', alertIcon: '✅', alertTone: 'green',
        alertTitle: (n) => `${n} commande${n > 1 ? 's' : ''} validée${n > 1 ? 's' : ''} par le client`,
      },
      {
        to: 'devis', label: 'Devis', icon: '📋',
        alert: 'demande', alertIcon: '📬', alertTone: 'blue',
        alertTitle: (n) => `${n} nouvelle${n > 1 ? 's' : ''} demande${n > 1 ? 's' : ''} depuis votre arrivée`,
      },
      { to: 'attente',  label: 'Liste d\'attente',     icon: '⏳' },
    ],
  },
  {
    group: 'Pilotage',
    items: [
      { to: 'tickets',  label: 'Tickets de pesée', icon: '🧾' },
      { to: 'planning', label: 'Planning',     icon: '📅' },
      { to: 'previsionnel', label: 'Prévisionnel', icon: '📈' },
      { to: 'stats',    label: 'Statistiques', icon: '📊' },
    ],
  },
  {
    group: 'Contenu',
    items: [
      { to: 'produits', label: 'Produits',      icon: '🛍️' },
      { to: 'fournisseurs', label: 'Fournisseurs', icon: '🚜' },
      { to: 'modeles-mail', label: 'Modèles de mail', icon: '✉️' },
      { to: 'avis',     label: 'Avis',          icon: '⭐' },
      { to: 'feedback', label: 'Feedback site', icon: '💬' },
    ],
  },
]
const ALL_ITEMS = NAV.flatMap((g) => g.items)

/* ════════════════════════════════════════════════════════════
   CONNEXION
   ════════════════════════════════════════════════════════════ */
const AuthWrap = styled.div`
  min-height: 100vh; background: ${t.bg};
  display: flex; align-items: center; justify-content: center; padding: 24px;
`
const AuthCard = styled.form`
  width: 100%; max-width: 360px;
  background: ${t.surface};
  border: 1px solid ${t.line};
  border-radius: 12px;
  padding: 32px 30px;
  animation: ${scaleIn} 0.24s ${ease} both;
`
const AuthMark = styled.div`
  font-size: 1.4rem; margin-bottom: 14px;
`
const AuthTitle = styled.h1`
  font-family: ${t.serif}; font-size: 1.16rem; font-weight: 660;
  letter-spacing: -0.032em; color: ${t.text}; margin: 0 0 4px;
  em { font-style: normal; color: ${t.gold}; }
`
const AuthSub = styled.p`
  font-family: ${t.font}; font-size: 0.775rem; color: ${t.textFaint};
  margin: 0 0 26px;
`
const AuthError = styled.p`
  font-family: ${t.font}; font-size: 0.78rem; color: ${t.red};
  margin: 13px 0 0;
`

function AuthScreen() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const handleLogin = async (e) => {
    e.preventDefault()
    setLoading(true); setError('')
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) setError('Email ou mot de passe incorrect')
    setLoading(false)
  }

  return (
    <AuthWrap>
      <AuthCard onSubmit={handleLogin}>
        <AuthMark>🌾</AuthMark>
        <AuthTitle>Achat<em>Paille</em></AuthTitle>
        <AuthSub>Espace d'administration</AuthSub>

        <Field>
          <Label>Email</Label>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="vous@exemple.com" autoComplete="username" required />
        </Field>
        <Field>
          <Label>Mot de passe</Label>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" autoComplete="current-password" required />
        </Field>

        <Btn type="submit" $variant="gold" $size="lg" $block disabled={loading} style={{ marginTop: 4 }}>
          {loading ? <><Spinner /> Connexion…</> : 'Se connecter'}
        </Btn>

        {error && <AuthError>{error}</AuthError>}
      </AuthCard>
    </AuthWrap>
  )
}

/* ════════════════════════════════════════════════════════════
   COQUE
   ════════════════════════════════════════════════════════════ */
const Shell = styled.div`
  min-height: 100vh;
  background: ${t.surface};
  display: grid;
  grid-template-columns: 216px 1fr;
  grid-template-rows: auto 1fr;
  grid-template-areas: 'nav top' 'nav main';
  @media (max-width: 940px) {
    grid-template-columns: 1fr;
    grid-template-areas: 'top' 'main';
  }
`
const Top = styled.header`
  grid-area: top;
  /* En plein écran sur iPhone, la barre d'état recouvre le haut de la page */
  height: calc(52px + env(safe-area-inset-top));
  padding: env(safe-area-inset-top) calc(22px + env(safe-area-inset-right)) 0 calc(22px + env(safe-area-inset-left));
  display: flex; align-items: center; justify-content: space-between; gap: 14px;
  background: ${t.surface};
  border-bottom: 1px solid ${t.line};
  @media (max-width: 940px) {
    padding: env(safe-area-inset-top) calc(14px + env(safe-area-inset-right)) 0 calc(14px + env(safe-area-inset-left));
  }
`
const TopLeft = styled.div`display: flex; align-items: center; gap: 10px; min-width: 0;`
const Crumb = styled.div`
  font-family: ${t.serif}; font-size: 0.83rem; font-weight: 600;
  letter-spacing: -0.022em; color: ${t.text};
  display: flex; align-items: center; gap: 7px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
`
const TopRight = styled.div`display: flex; align-items: center; gap: 10px;`
const Cloche = styled.button`
  width: 32px; height: 32px; border-radius: ${t.rSm};
  border: 1px solid ${(p) => (p.$on ? '#CFE2DC' : t.line)};
  background: ${(p) => (p.$on ? t.goldWash : t.surface)};
  cursor: pointer; font-size: 0.92rem; line-height: 1;
  opacity: ${(p) => (p.$on ? 1 : 0.55)};
  transition: all 0.14s;
  &:hover { border-color: ${t.gold}; opacity: 1; }
`
const UserMail = styled.span`
  font-family: ${t.font}; font-size: 0.735rem; color: ${t.textFaint};
  @media (max-width: 820px) { display: none; }
`
const Burger = styled.button`
  display: none;
  background: ${t.surface}; border: 1px solid ${t.line};
  width: 30px; height: 30px; border-radius: ${t.rSm};
  cursor: pointer; color: ${t.text}; font-size: 0.85rem;
  @media (max-width: 940px) { display: flex; align-items: center; justify-content: center; }
`

const Nav = styled.nav`
  grid-area: nav;
  background: ${t.bg};
  border-right: 1px solid ${t.line};
  padding: 16px 0 24px;
  overflow-y: auto;
  @media (max-width: 940px) {
    position: fixed; top: 0; bottom: 0; left: 0;
    width: calc(236px + env(safe-area-inset-left));
    padding-top: calc(16px + env(safe-area-inset-top));
    padding-bottom: calc(24px + env(safe-area-inset-bottom));
    padding-left: env(safe-area-inset-left);
    z-index: 2100;
    transform: translateX(${(p) => (p.$open ? '0' : '-100%')});
    transition: transform 0.22s ${ease};
    box-shadow: ${(p) => (p.$open ? '0 0 40px rgba(22,24,28,0.2)' : 'none')};
  }
`
const Scrim = styled.div`
  display: none;
  @media (max-width: 940px) {
    display: ${(p) => (p.$open ? 'block' : 'none')};
    position: fixed; inset: 0; z-index: 2050;
    background: rgba(22,24,28,0.3);
    animation: ${fadeIn} 0.16s ease both;
  }
`
const Brand = styled.div`
  display: flex; align-items: center; gap: 8px;
  padding: 0 19px 18px;
  font-family: ${t.serif}; font-size: 0.9rem; font-weight: 640;
  letter-spacing: -0.024em; color: ${t.text};
  em { font-style: normal; color: ${t.gold}; }
`
const GroupLabel = styled.div`
  font-family: ${t.font}; font-size: 0.605rem; font-weight: 600;
  letter-spacing: 0.1em; text-transform: uppercase; color: ${t.textFaint};
  padding: 0 19px; margin: 20px 0 7px;
  &:first-of-type { margin-top: 4px; }
`
const NavLink = styled.button`
  width: calc(100% - 16px);
  margin: 0 8px 3px;
  text-align: left;
  display: flex; align-items: center; gap: 10px;
  /* Hauteur doublée : cible tactile confortable sans élargir la colonne */
  min-height: 48px;
  padding: 9px 11px;
  border: none; border-radius: ${t.rSm};
  cursor: pointer;
  font-family: ${t.font}; font-size: 0.8rem; letter-spacing: -0.01em;
  transition: background 0.14s, color 0.14s;

  background: ${(p) => (p.$on ? t.gold : 'transparent')};
  color: ${(p) => (p.$on ? '#fff' : t.textSoft)};
  font-weight: ${(p) => (p.$on ? 560 : 480)};

  &:hover {
    background: ${(p) => (p.$on ? t.goldDeep : t.surfaceSunk)};
    color: ${(p) => (p.$on ? '#fff' : t.text)};
  }
  /* Sur l'onglet actif (fond vert plein), la pastille repasse en blanc translucide */
  ${(p) => p.$on && css`
    ${CountBadge} {
      background: rgba(255,255,255,0.2);
      color: #fff;
      border-color: rgba(255,255,255,0.3);
    }
  `}
`
const NavIcon = styled.span`font-size: 0.98rem; width: 19px; text-align: center; flex-shrink: 0; opacity: 0.9;`
/* Le libellé passe sur deux lignes plutôt que d'élargir la colonne */
const NavText = styled.span`
  flex: 1; min-width: 0;
  line-height: 1.25;
  white-space: normal;
`

const Main = styled.main`
  grid-area: main;
  min-width: 0;
  padding:
    22px
    calc(26px + env(safe-area-inset-right))
    calc(56px + env(safe-area-inset-bottom))
    calc(26px + env(safe-area-inset-left));
  @media (max-width: 940px) {
    padding:
      16px
      calc(14px + env(safe-area-inset-right))
      calc(48px + env(safe-area-inset-bottom))
      calc(14px + env(safe-area-inset-left));
  }
`

function AdminShell({ session }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { confirm, toast } = useUI()
  const [drawer, setDrawer] = useState(false)
  const [alertes, setAlertes] = useState(alertesActives)
  /* Compteur de demandes arrivées depuis l'ouverture : la pastille du
     menu doit se voir même si la page Devis n'est pas affichée. */
  const [nouvellesDemandes, setNouvellesDemandes] = useState(0)
  const [rappelAlert, setRappelAlert] = useState(0)
  const [validationAlert, setValidationAlert] = useState(0)

  const segment = location.pathname.replace(/^\/admin\/?/, '').split('/')[0] || 'clients'

  const basculerSon = async () => {
    const { actif, notifications } = await basculerAlertes()
    setAlertes(actif)
    if (!actif) return toast('Alertes coupées')
    toast(notifications
      ? 'Alertes activées — son et notification'
      : 'Alertes sonores activées. Les bannières système sont refusées par le navigateur.')
  }

  /* Une demande de devis arrive : le site public insère dans `quotes`.
     L'écoute est posée sur la coquille pour valoir quelle que soit la
     page ouverte, et ne dépend pas des alertes sonores — le bandeau,
     lui, s'affiche toujours. */
  useEffect(() => {
    const canal = supabase.channel('nouvelles-demandes')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'quotes' }, ({ new: q }) => {
        setNouvellesDemandes((n) => n + 1)
        if (alertesActives()) jouerCarillon()
        const qui = q?.name?.trim() || 'Un client'
        toast(`📬 Nouvelle demande de devis — ${qui}`)
        notifier('Nouvelle demande de devis', `${qui}${q?.email ? ` · ${q.email}` : ''}`,
          () => navigate(`/admin/devis/${q.id}`))
      })
      .subscribe()
    return () => { supabase.removeChannel(canal) }
  }, [navigate, toast])

  useEffect(() => {
    const today = new Date()
    const in3days = new Date(today); in3days.setDate(today.getDate() + 3)
    supabase.from('clients').select('rappel_date, rappel_fait')
      .eq('rappel_fait', false)
      .lte('rappel_date', in3days.toISOString().split('T')[0])
      .then(({ data }) => setRappelAlert(data?.length || 0))
  }, [])

  useEffect(() => {
    supabase.from('orders').select('id', { count: 'exact' }).eq('status', 'validated')
      .then(({ count }) => setValidationAlert(count || 0))
  }, [])

  useEffect(() => { setDrawer(false) }, [location.pathname])

  const alerts = { rappel: rappelAlert, validation: validationAlert, demande: nouvellesDemandes }

  const go = (to) => {
    if (to === 'carnet') setValidationAlert(0)
    if (to === 'clients') setRappelAlert(0)
    if (to === 'devis') setNouvellesDemandes(0)
    navigate(`/admin/${to}`)
  }

  const logout = async () => {
    const ok = await confirm({
      title: 'Se déconnecter ?',
      body: 'Vous devrez saisir à nouveau votre email et votre mot de passe pour revenir.',
      icon: '👋', tone: 'gold', confirmLabel: 'Se déconnecter',
    })
    if (ok) supabase.auth.signOut()
  }

  const current = ALL_ITEMS.find((i) => i.to === segment)

  return (
    <Shell>
      <Top>
        <TopLeft>
          <Burger onClick={() => setDrawer((o) => !o)} aria-label="Menu">{drawer ? '✕' : '☰'}</Burger>
          <Crumb><span aria-hidden="true">{current?.icon}</span>{current?.label}</Crumb>
        </TopLeft>
        <TopRight>
          <Cloche $on={alertes} onClick={basculerSon}
            title={alertes ? 'Alertes sonores activées' : 'Alertes sonores coupées'}>
            {alertes ? '🔔' : '🔕'}
          </Cloche>
          <UserMail>{session.user.email}</UserMail>
          <Btn $variant="ghost" $size="sm" onClick={logout}>Déconnexion</Btn>
        </TopRight>
      </Top>

      <Scrim $open={drawer} onClick={() => setDrawer(false)} />

      <Nav $open={drawer}>
        <Brand>🌾 Achat<em>Paille</em></Brand>
        {NAV.map((g) => (
          <div key={g.group}>
            <GroupLabel>{g.group}</GroupLabel>
            {g.items.map((item) => {
              const count = item.alert ? alerts[item.alert] : 0
              return (
                <NavLink key={item.to} $on={segment === item.to} onClick={() => go(item.to)}>
                  <NavIcon aria-hidden="true">{item.icon}</NavIcon>
                  <NavText>{item.label}</NavText>
                  {count > 0 && (
                    <CountBadge $tone={item.alertTone} title={item.alertTitle(count)}>
                      <b aria-hidden="true">{item.alertIcon}</b>{count}
                    </CountBadge>
                  )}
                </NavLink>
              )
            })}
          </div>
        ))}
      </Nav>

      <Main>
        <Routes>
          <Route index element={<Navigate to="clients" replace />} />
          <Route path="clients"    element={<ClientsAdmin />} />
          <Route path="carnet"     element={<CarnetAdmin onValidationAlert={() => setValidationAlert((v) => v + 1)} />} />
          <Route path="carnet/:id" element={<CarnetDetail onValidationAlert={() => setValidationAlert((v) => v + 1)} />} />
          <Route path="carnet/:id/livraison" element={<CarnetLivraison onValidationAlert={() => setValidationAlert((v) => v + 1)} />} />
          <Route path="devis"      element={<DevisAdmin />} />
          <Route path="devis/:id"  element={<DevisDetail />} />
          <Route path="attente"    element={<ListeAttenteAdmin />} />
          <Route path="tickets"    element={<TicketsAdmin />} />
          <Route path="planning"   element={<PlanningAdmin />} />
          <Route path="previsionnel" element={<PrevisionnelAdmin />} />
          <Route path="stats"      element={<StatistiquesAdmin />} />
          <Route path="produits"   element={<ProduitsAdmin />} />
          <Route path="fournisseurs" element={<FournisseursAdmin />} />
          <Route path="modeles-mail" element={<ModelesMailAdmin />} />
          <Route path="avis"       element={<AvisAdmin />} />
          <Route path="feedback"   element={<FeedbackAdmin />} />
          <Route path="*"          element={<Navigate to="/admin/clients" replace />} />
        </Routes>
      </Main>
    </Shell>
  )
}

/* ════════════════════════════════════════════════════════════
   RACINE
   ════════════════════════════════════════════════════════════ */
/* Ajouté à l'écran d'accueil iOS, le raccourci suit le manifeste du site,
   dont le start_url pointe sur la vitrine. On bascule sur un manifeste
   propre à l'admin tant que celle-ci est affichée, et on remet l'original
   en sortant pour ne pas altérer le site public. */
function useAdminManifest() {
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]')
    const original = link?.getAttribute('href')
    link?.setAttribute('href', '/admin-manifest.json')

    const metas = [
      ['apple-mobile-web-app-capable', 'yes'],
      ['apple-mobile-web-app-status-bar-style', 'default'],
      ['apple-mobile-web-app-title', 'Admin Achat Paille'],
    ].map(([name, content]) => {
      const m = document.createElement('meta')
      m.name = name
      m.content = content
      document.head.appendChild(m)
      return m
    })

    return () => {
      if (original) link?.setAttribute('href', original)
      metas.forEach((m) => m.remove())
    }
  }, [])
}

export default function Admin() {
  const [session, setSession] = useState(null)
  const [checking, setChecking] = useState(true)
  useAdminManifest()

  useEffect(() => {
    // Garde-fou : si getSession traîne (lock auth bloqué), on débloque l'UI au bout de 3s.
    const fallback = setTimeout(() => setChecking(false), 3000)
    supabase.auth.getSession()
      .then(({ data: { session } }) => { setSession(session); setChecking(false); clearTimeout(fallback) })
      .catch(() => { setChecking(false); clearTimeout(fallback) })
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, s) => setSession(s))
    return () => { subscription.unsubscribe(); clearTimeout(fallback) }
  }, [])

  return (
    <>
      <AdminGlobal />
      {checking ? null : (
        <AdminUIProvider>
          {session ? <AdminShell session={session} /> : <AuthScreen />}
        </AdminUIProvider>
      )}
    </>
  )
}
